import {
  Alert,
  Box,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  useTheme,
} from "@mui/material";
import * as React from "react";

import { EntityCustomFields } from "@/shared/components/CustomFields";
import {
  UpdateDeviceTags,
  UpdateDeviceMetadata,
} from "@/shared/components/devices";
import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal";
import { TargetEntityType } from "@/shared/domain/customFields";
import { useDevice } from "@/shared/domain/devices/hooks/useDevice";
import { useUpdateDevice } from "@/shared/domain/devices/hooks/useUpdateDevice";

import { useDeviceContext } from "../Device/useDeviceContext";

/**
 * Details tab of the device-detail route. Renders identifying fields
 * (id, created/modified timestamps and principals) plus inline editors
 * for tags, metadata, and custom fields.
 */
export function DeviceDetails() {
  const theme = useTheme();
  const { deviceId, org } = useDeviceContext();
  const currentOrgId = org?.org_id;
  const deviceQuery = useDevice(deviceId, currentOrgId);
  const { mutateAsync: updateDevice } = useUpdateDevice();
  const [customFieldsError, setCustomFieldsError] =
    React.useState<Error | null>(null);

  const device = deviceQuery.data;

  const dataLoading = deviceQuery.isLoading;

  return (
    <>
      {!device && dataLoading && (
        <Box marginTop={theme.spacing(2)}>
          <Skeleton variant="rounded" height="300px" />
        </Box>
      )}

      {deviceQuery.error && (
        <Box
          sx={{
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(2),
          }}
        >
          <Alert severity="error">{deviceQuery.error.message}</Alert>
        </Box>
      )}

      {customFieldsError && (
        <Box sx={{ marginTop: theme.spacing(2) }}>
          <Alert severity="error" onClose={() => setCustomFieldsError(null)}>
            {customFieldsError.message}
          </Alert>
        </Box>
      )}

      {device && !dataLoading && (
        <Box>
          <List sx={{ width: "100%" }} disablePadding>
            <ListItem>
              <ListItemText
                primary={"Device ID"}
                secondary={device.device_id}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={"Created"}
                secondary={new Date(device.created).toLocaleString()}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={"Created By"}
                secondary={
                  <RobotoPrincipalUnderlineLink
                    principal={device.created_by}
                    icon
                  />
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={"Modified"}
                secondary={new Date(device.modified).toLocaleString()}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={"Modified By"}
                secondary={
                  <RobotoPrincipalUnderlineLink
                    principal={device.modified_by}
                    icon
                  />
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={"Tags"}
                secondary={
                  currentOrgId ? (
                    <UpdateDeviceTags
                      device={device}
                      resourceOwnerId={currentOrgId}
                    />
                  ) : (
                    "Loading..."
                  )
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={"Metadata"}
                secondary={
                  currentOrgId ? (
                    <UpdateDeviceMetadata
                      device={device}
                      resourceOwnerId={currentOrgId}
                    />
                  ) : (
                    "Loading..."
                  )
                }
              />
            </ListItem>
            <ListItem sx={{ display: "block" }}>
              <EntityCustomFields
                entityType={TargetEntityType.Device}
                orgId={device.org_id}
                values={device.custom_fields}
                commit={(changeset) =>
                  updateDevice({
                    deviceId,
                    resourceOwnerId: device.org_id,
                    updates: { custom_fields_changeset: changeset },
                  })
                }
                onError={setCustomFieldsError}
                variant="plain"
              />
            </ListItem>
          </List>
        </Box>
      )}
    </>
  );
}
